import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Code, Download, FileText, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApi } from "../api";
import { useCurrentUser } from "../auth";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Input,
  InlineText,
  Switch,
  Textarea,
  Field,
  FileUpload,
  Select,
  SelectItem,
  Alert,
  Loading,
  ConfirmDialog,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from "../components/ui";
import { StatementRead, Pictograms } from "./StatementRead";
import { languageName } from "./types";
import type {
  ProductRecord,
  WebsiteRecord,
  HistoryVersion,
  LanguageVersion,
} from "./types";
import {
  analyzeStatementXmlSegments,
  serializeStatementXmlSegments,
} from "../components/statement-nodes/parse";
import { extractApiErrorMessage } from "../apiErrors";
import { useLocalDraft } from "./useLocalDraft";

export function HistoryPanel({
  version,
  onChanged,
}: {
  version: LanguageVersion;
  onChanged: () => Promise<void>;
}) {
  const api = useApi();
  const { t } = useTranslation("work");
  const { permissions } = useCurrentUser();
  const [historyPage, setHistoryPage] = useState(1);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [items, setItems] = useState<HistoryVersion[]>([]);
  const [preview, setPreview] = useState<HistoryVersion | null>(null);
  const [current, setCurrent] = useState<HistoryVersion | null>(null);
  const [undo, setUndo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const fetchHistory = async () => {
    const rows = await api.get(`/translations/${version.head_id}/versions`);
    setItems(rows);
    setHistoryPage(1);
    setHasEarlier(rows.length === 50);
    const [latest, previous] = await Promise.all([
      rows[0]
        ? api.get(
            `/translations/${version.head_id}/versions/${rows[0].version}`,
          )
        : null,
      rows[1]
        ? api.get(
            `/translations/${version.head_id}/versions/${rows[1].version}`,
          )
        : null,
    ]);
    setCurrent(latest);
    setPreview(previous || latest);
  };
  useEffect(() => {
    void fetchHistory().catch(() => setError(t("loadError")));
  }, [api, version.head_id, version.latest_version]);
  const earlier = async () => {
    setBusy(true);
    try {
      const rows = await api.get(
        `/translations/${version.head_id}/versions?page=${historyPage + 1}&size=50`,
      );
      setItems((old) => [...old, ...rows]);
      setHistoryPage(historyPage + 1);
      setHasEarlier(rows.length === 50);
    } catch {
      setError(t("loadError"));
    } finally {
      setBusy(false);
    }
  };
  const choose = async (item: HistoryVersion) => {
    setBusy(true);
    setError("");
    try {
      setPreview(
        await api.get(
          `/translations/${version.head_id}/versions/${item.version}`,
        ),
      );
    } catch {
      setError(t("loadError"));
    } finally {
      setBusy(false);
    }
  };
  const restore = async (id: string, isUndo = false) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const fresh = await api.get(`/translations/${version.head_id}/versions`);
      if (fresh[0]?.id !== current?.id) {
        await fetchHistory();
        setError(t("history.changed"));
        return;
      }
      await api.post(
        `/translations/${version.head_id}/rollback`,
        { version_id: id },
        { headers: { "Idempotency-Key": crypto.randomUUID() } },
      );
      setUndo(isUndo ? null : current?.id || null);
      setMessage(t(isUndo ? "history.undone" : "history.restored"));
      try {
        await onChanged();
        await fetchHistory();
      } catch {
        setError(t("savedRefreshFailed"));
      }
    } catch (e) {
      setError(`${t("history.failed")} ${extractApiErrorMessage(e) || ""}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-label={t("history.title")}>
      {error && <Alert variant="error">{error}</Alert>}
      {message && (
        <Alert variant="success">
          {message}{" "}
          {undo && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void restore(undo, true)}
            >
              {t("history.undo")}
            </Button>
          )}
        </Alert>
      )}
      {!items.length ? (
        <Loading />
      ) : (
        <div className="history-layout">
          <nav className="history-list" aria-label={t("history.versions")}>
            {items.map((item) => (
              <Button
                key={item.id}
                variant="ghost"
                aria-pressed={preview?.id === item.id}
                disabled={busy}
                onClick={() => void choose(item)}
              >
                {t("version", { number: item.version })}
                {item.id === current?.id ? ` · ${t("history.current")}` : ""}
              </Button>
            ))}
            {hasEarlier && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => void earlier()}
              >
                {t("history.earlier")}
              </Button>
            )}
          </nav>
          <div>
            {preview && (
              <>
                <div className="work-actions">
                  <strong>{t("version", { number: preview.version })}</strong>
                  <span className="work-muted">
                    {new Date(preview.created_at).toLocaleString()}
                  </span>
                  {permissions.includes("translation.rollback") &&
                    preview.id !== current?.id && (
                      <Button
                        disabled={busy}
                        onClick={() => void restore(preview.id)}
                      >
                        {t("history.restore")}
                      </Button>
                    )}
                </div>
                <p className="work-muted">{t("history.effect")}</p>
                {preview.id !== current?.id && (
                  <details open>
                    <summary>{t("history.differences")}</summary>
                    <StatementChanges
                      before={current?.text || ""}
                      after={preview.text || ""}
                      language={version.language}
                    />
                  </details>
                )}
                {preview.id === current?.id && (
                  <StatementRead
                    text={preview.text || ""}
                    language={version.language}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export function WebsitePanel({
  product,
  website,
  setWebsite,
  startOpen = false,
}: {
  startOpen?: boolean;
  product: ProductRecord;
  website: WebsiteRecord | null;
  setWebsite: (value: WebsiteRecord) => void;
}) {
  const api = useApi();
  const { t, i18n } = useTranslation("work");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rotate, setRotate] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(startOpen);
  const [options, setOptions] = useState({
    language: product.base_language,
    title: "",
    compact: false,
    show_pictograms: true,
    show_codes: true,
    show_language_selector: true,
    branding: true,
  });
  const path =
    website?.embed_path ||
    (website?.public_key
      ? `/embed/clp/${encodeURIComponent(website.public_key)}`
      : "");
  const query = new URLSearchParams(
    Object.entries(options).map(([key, value]) => [key, String(value)]),
  );
  const url = path ? `${window.location.origin}${path}?${query}` : "";
  const code = url
    ? `<iframe src="${url.replace(/&/g, "&amp;").replace(/"/g, "&quot;")}" title="${t("websiteFrameTitle")}" width="100%" height="420" style="border:0" loading="lazy"></iframe>`
    : "";
  const act = async (kind: "enable" | "disable" | "rotate") => {
    if (kind === "rotate" && !website?.enabled) return;
    setBusy(true);
    setError("");
    try {
      const result =
        kind === "disable"
          ? await api.del(`/products/${product.id}/embed`)
          : await api.post(
              `/products/${product.id}/embed${kind === "rotate" ? "/rotate" : ""}`,
              {},
            );
      setWebsite(result);
      setRotate(false);
      setMessage(t(kind === "disable" ? "websiteOffSaved" : "websiteOnSaved"));
    } catch {
      setError(t("actionError"));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setMessage(t("copied"));
    } catch {
      setError(t("copyFailed"));
    }
  };
  return (
    <Card aria-label={t("website")}>
      <CardHeader
        title={t("website")}
        hint={t(website?.enabled ? "onWebsite" : "notOnWebsite")}
      >
        <Switch
          aria-label={t("websiteAvailability")}
          checked={!!website?.enabled}
          disabled={busy}
          onCheckedChange={(on) => void act(on ? "enable" : "disable")}
        />
      </CardHeader>
      <CardBody className="rail-stack">
        {url && (
          <Popover
            open={optionsOpen}
            onOpenChange={(open) => {
              // The confirmation takes focus; keep its trigger to return to.
              if (open || !rotate) setOptionsOpen(open);
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="rail-button">
                <Code aria-hidden="true" />
                {t("websiteSettings")}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="product-website-options"
              aria-label={t("websiteSettings")}
              align="start"
              side="left"
            >
              <div className="product-section-heading">
                <h2>{t("websiteSettings")}</h2>
                <PopoverClose asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="vl-button-icon"
                    aria-label={t("closePanel")}
                  >
                    <X aria-hidden="true" />
                  </Button>
                </PopoverClose>
              </div>
              <div className="work-form">
                <p className="work-muted">
                  {t("websiteTiming")}{" "}
                  <a
                    className="text-link"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("openWebsite")}
                  </a>
                </p>
                <div className="work-form two-columns">
                  <Field
                    label={t("websiteLanguage")}
                    htmlFor="website-language"
                  >
                    <Select
                      id="website-language"
                      value={options.language}
                      onValueChange={(value) =>
                        setOptions({ ...options, language: value })
                      }
                    >
                      {(
                        product.translation_languages || [product.base_language]
                      ).map((language) => (
                        <SelectItem key={language} value={language}>
                          {languageName(language, i18n.language)}
                        </SelectItem>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t("websiteTitle")} htmlFor="website-title">
                    <Input
                      id="website-title"
                      value={options.title}
                      onChange={(e) =>
                        setOptions({ ...options, title: e.target.value })
                      }
                    />
                  </Field>
                </div>
                <div className="website-option-list">
                  {(
                    [
                      "compact",
                      "show_pictograms",
                      "show_codes",
                      "show_language_selector",
                      "branding",
                    ] as const
                  ).map((key) => (
                    <label className="work-inline-note" key={key}>
                      <Checkbox
                        checked={options[key]}
                        onCheckedChange={(nextChecked) =>
                          setOptions({
                            ...options,
                            [key]: nextChecked === true,
                          })
                        }
                      />
                      {t(`websiteOptions.${key}`)}
                    </label>
                  ))}
                </div>
                <Field label={t("websiteCode")} htmlFor="website-code">
                  <Textarea
                    className="website-code"
                    id="website-code"
                    value={code}
                    readOnly
                  />
                </Field>
                <div className="work-actions">
                  <Button variant="outline" onClick={() => void copy()}>
                    {t("copyCode")}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={!website?.enabled || busy}
                    onClick={() => setRotate(true)}
                  >
                    {t("rotateLink")}
                  </Button>
                </div>
                {!website?.enabled && (
                  <p className="work-muted">{t("rotateRequiresWebsite")}</p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        {message && (
          <p className="product-save-feedback" role="status">
            {message}
          </p>
        )}
      </CardBody>
      <ConfirmDialog
        open={rotate}
        onOpenChange={setRotate}
        title={t("rotateLink")}
        description={t("rotateWarning")}
        confirmLabel={t("rotateLink")}
        pending={busy}
        onConfirm={() => act("rotate")}
      />
    </Card>
  );
}

type DetailField = "name" | "sku" | "ean";
type DetailScope = DetailField | "pictograms";

function useProductDetails(
  product: ProductRecord,
  onChanged: () => Promise<void>,
) {
  const api = useApi();
  const { t } = useTranslation("work");
  const { permissions } = useCurrentUser();
  const initial = {
    name: product.name,
    sku: product.sku || "",
    ean: product.ean || "",
    ghs_pictograms: product.ghs_pictograms || [],
  };
  // Retain the existing recovery key, including drafts from the combined editor.
  const draft = useLocalDraft(
    `product:${product.id}:identity`,
    initial,
    product.updated_at || JSON.stringify(initial),
  );
  const value = draft.value;
  const [busy, setBusy] = useState<DetailScope | null>(null);
  const [feedback, setFeedback] = useState<{
    scope: "identity" | "pictograms";
    error: boolean;
    text: string;
  } | null>(null);
  const canEdit = permissions.includes("product.update");
  const pictogramsDirty =
    JSON.stringify(value.ghs_pictograms) !==
    JSON.stringify(initial.ghs_pictograms);
  const save = async (scope: DetailScope) => {
    if (busy || !canEdit) return false;
    const group = scope === "pictograms" ? "pictograms" : "identity";
    setBusy(scope);
    setFeedback(null);
    try {
      const saved = await api.patch(
        `/products/${product.id}`,
        scope === "pictograms"
          ? { ghs_pictograms: value.ghs_pictograms }
          : scope === "name"
            ? { name: value.name.trim() }
            : { [scope]: value[scope].trim() || null },
      );
      const savedValue = {
        name: saved.name,
        sku: saved.sku || "",
        ean: saved.ean || "",
        ghs_pictograms: saved.ghs_pictograms || [],
      };
      const field = scope === "pictograms" ? "ghs_pictograms" : scope;
      // Each save sends one field, so other unsaved edits stay in the draft.
      draft.rebase(savedValue, saved.updated_at || JSON.stringify(savedValue), {
        ...value,
        [field]: savedValue[field],
      });
      setFeedback({
        scope: group,
        error: false,
        text: t(scope === "pictograms" ? "pictogramsSaved" : "identity.saved"),
      });
      try {
        await onChanged();
      } catch {
        setFeedback({
          scope: group,
          error: true,
          text: t("savedRefreshFailed"),
        });
      }
      return true;
    } catch (e) {
      setFeedback({
        scope: group,
        error: true,
        text: `${t("saveFailed")} ${extractApiErrorMessage(e) || ""}`,
      });
      return false;
    } finally {
      setBusy(null);
    }
  };
  return {
    draft,
    value,
    initial,
    busy,
    feedback,
    setFeedback,
    canEdit,
    pictogramsDirty,
    save,
  };
}
export type ProductDetailsState = ReturnType<typeof useProductDetails>;

/** Gives the header and the pictogram card one shared recoverable draft. */
export function ProductDetailsScope({
  product,
  onChanged,
  children,
}: {
  product: ProductRecord;
  onChanged: () => Promise<void>;
  children: (details: ProductDetailsState) => React.ReactNode;
}) {
  return <>{children(useProductDetails(product, onChanged))}</>;
}

function SaveResult({
  details,
  scope,
}: {
  details: ProductDetailsState;
  scope: "identity" | "pictograms";
}) {
  const { feedback } = details;
  if (feedback?.scope !== scope) return null;
  return feedback.error ? (
    <Alert variant="error">{feedback.text}</Alert>
  ) : (
    <p className="product-save-feedback" role="status">
      {feedback.text}
    </p>
  );
}

export function ProductHeader({
  product,
  details,
  children,
}: {
  product: ProductRecord;
  details: ProductDetailsState;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation("work");
  const { draft, value, initial, canEdit } = details;
  const field = (key: DetailField, as?: "h1") => (
    <InlineText
      as={as}
      label={t(`identity.${key}`)}
      editLabel={t("identity.editField", { field: t(`identity.${key}`) })}
      value={value[key]}
      saved={initial[key]}
      placeholder={key === "name" ? undefined : t("identity.add")}
      required={key === "name"}
      editable={canEdit}
      onChange={(next) => {
        draft.setValue({ ...value, [key]: next });
        details.setFeedback(null);
      }}
      onCancel={() => draft.setValue({ ...value, [key]: initial[key] })}
      onCommit={() => details.save(key)}
    />
  );
  return (
    <header className="product-header">
      <Link className="work-title-link" to="/products">
        <ArrowLeft size={14} aria-hidden="true" />
        {t("backProducts")}
      </Link>
      <div className="product-header-row">
        <div className="product-heading">
          {field("name", "h1")}
          <div className="product-identifiers">
            {(["sku", "ean"] as const).map((key) => (
              <span key={key} className="product-identifier">
                <span className="product-identifier-label">
                  {t(`identity.${key}`)}
                </span>
                {field(key)}
              </span>
            ))}
          </div>
        </div>
        <div className="work-actions">{children}</div>
      </div>
      <SaveResult details={details} scope="identity" />
      {(draft.recovered || draft.stale) && (
        <Alert>
          <div className="vl-alert-row">
            <span>
              {draft.recovered && t("draft.recovered")}
              {draft.stale && ` ${t("draft.stale")}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => draft.reset(initial)}
            >
              {t("discard")}
            </Button>
          </div>
        </Alert>
      )}
      {draft.storageError && (
        <Alert variant="warning">{t("draft.storageError")}</Alert>
      )}
      {draft.stale && (
        <details>
          <summary>{t("draft.compareLatest")}</summary>
          <p>
            {[product.name, product.sku, product.ean]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <Pictograms selected={product.ghs_pictograms || []} />
        </details>
      )}
    </header>
  );
}

export function PictogramsCard({ details }: { details: ProductDetailsState }) {
  const { t } = useTranslation("work");
  const { draft, value, initial, busy, canEdit, pictogramsDirty } = details;
  return (
    <Card aria-label={t("pictograms")}>
      <CardHeader title={t("pictograms")} hint={t("sharedAcrossLanguages")} />
      <CardBody>
        <form
          className="rail-stack"
          onSubmit={(event) => {
            event.preventDefault();
            void details.save("pictograms");
          }}
        >
          <Pictograms
            selected={value.ghs_pictograms}
            disabled={!!busy}
            onChange={
              canEdit
                ? (codes) => draft.setValue({ ...value, ghs_pictograms: codes })
                : undefined
            }
          />
          {!canEdit && !value.ghs_pictograms.length && (
            <p className="work-muted">{t("noPictograms")}</p>
          )}
          {canEdit && pictogramsDirty && (
            <div className="work-actions">
              <Button size="sm" type="submit" disabled={!!busy}>
                {busy === "pictograms" ? t("saving") : t("savePictograms")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={!!busy}
                onClick={() => {
                  draft.setValue({
                    ...value,
                    ghs_pictograms: initial.ghs_pictograms,
                  });
                  details.setFeedback(null);
                }}
              >
                {t("discardPictograms")}
              </Button>
            </div>
          )}
          <SaveResult details={details} scope="pictograms" />
        </form>
      </CardBody>
    </Card>
  );
}

export function SourceFilePanel({
  product,
  onChanged,
  onExtract,
}: {
  product: ProductRecord;
  onChanged: () => Promise<void>;
  onExtract: (
    text: string,
    uploadId: string,
    extractionId: string,
    pictograms: string[],
  ) => void;
}) {
  const api = useApi();
  const { t, i18n } = useTranslation("work");
  const { permissions } = useCurrentUser();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [languageWarning, setLanguageWarning] = useState("");
  const [uploaded, setUploaded] = useState<{
    id: string;
    original_filename: string;
  } | null>(null);
  const [remove, setRemove] = useState(false);
  const [message, setMessage] = useState("");
  const canUpload = permissions.includes(
    product.sds ? "product.sds.replace" : "product.sds.add",
  );
  const canExtract = canUpload && permissions.includes("translation.edit");
  const [autoExtract, setAutoExtract] = useState(true);
  const [uploading, setUploading] = useState(false);
  const extract = async (fileId = uploaded?.id || product.sds?.file_id) => {
    if (!fileId) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("source_language", product.base_language);
      const result = await api.postFormData(
        `/files/sds/${fileId}/extract-clp`,
        body,
      );
      if (!result.extracted_clp_text) throw new Error();
      onExtract(
        result.extracted_clp_text,
        fileId,
        result.id,
        result.ghs_codes || [],
      );
      setMessage(t("sds.extractedLocal"));
      setLanguageWarning(
        result.detected_language &&
          result.detected_language !== product.base_language
          ? t("sds.languageDiffers", {
              detected: languageName(result.detected_language, i18n.language),
              source: languageName(product.base_language, i18n.language),
            })
          : "",
      );
    } catch {
      setError(t("sds.extractFailed"));
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file?: File) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError(t("sds.tooLarge"));
      return;
    }
    if (!/\.(pdf|docx?)$/i.test(file.name)) {
      setError(t("sds.types"));
      return;
    }
    setUploading(true);
    setBusy(true);
    setError("");
    let stored: { id: string; original_filename: string };
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("extract_label_text_from_clp", "false");
      stored = await api.postFormData("/files/sds", body);
      setUploaded(stored);
    } catch {
      setError(t("sds.uploadFailed"));
      return;
    } finally {
      setUploading(false);
      setBusy(false);
    }
    if (canExtract && autoExtract) await extract(stored.id);
  };
  const attach = async () => {
    if (!uploaded) return;
    setBusy(true);
    setError("");
    try {
      await api.put(`/products/${product.id}/sds`, {
        sds_file_id: uploaded.id,
      });
      setUploaded(null);
      setMessage(t("sds.attached"));
      try {
        await onChanged();
      } catch {
        setError(t("savedRefreshFailed"));
      }
    } catch {
      setError(t("sds.attachFailed"));
    } finally {
      setBusy(false);
    }
  };
  const download = async () => {
    setError("");
    try {
      const response = await api.getResponse(
        `/products/${product.id}/sds/download`,
      );
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = product.sds?.original_filename || "sds.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError(t("sds.downloadFailed"));
    }
  };
  const deleteFile = async () => {
    setBusy(true);
    try {
      await api.del(`/products/${product.id}/sds`);
      setRemove(false);
      setMessage(t("sds.removed"));
      try {
        await onChanged();
      } catch {
        setError(t("savedRefreshFailed"));
      }
    } catch {
      setError(t("actionError"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card aria-label={t("sds.title")}>
      <CardHeader title={t("sds.title")} hint={t("sharedAcrossLanguages")} />
      <CardBody className="rail-stack">
        {error && <Alert variant="error">{error}</Alert>}
        {languageWarning && <Alert variant="warning">{languageWarning}</Alert>}
        {message && <Alert>{message}</Alert>}
        {product.sds && (
          <div className="sds-file">
            <FileText aria-hidden="true" />
            <span title={product.sds.original_filename}>
              {product.sds.original_filename}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="vl-button-icon"
              disabled={busy}
              aria-label={t("sds.download")}
              title={t("sds.download")}
              onClick={() => void download()}
            >
              <Download aria-hidden="true" />
            </Button>
            {permissions.includes("product.sds.delete") && (
              <Button
                variant="ghost"
                size="sm"
                className="vl-button-icon"
                disabled={busy}
                aria-label={t("sds.remove")}
                title={t("sds.remove")}
                onClick={() => setRemove(true)}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            )}
          </div>
        )}
        {canUpload && (
          <FileUpload
            id="source-file"
            compact
            label={t(product.sds ? "sds.replace" : "sds.upload")}
            hint={t("sds.types")}
            browseLabel={t("sds.browse")}
            changeLabel={t("sds.change")}
            busyLabel={t("sds.uploading")}
            filename={uploaded?.original_filename}
            disabled={busy}
            uploading={uploading}
            onFileSelect={(file) => void upload(file)}
          />
        )}
        {canExtract && !uploaded && (
          <label className="rail-switch">
            <Switch
              checked={autoExtract}
              disabled={busy}
              onCheckedChange={setAutoExtract}
            />
            <span>{t("sds.autoExtract")}</span>
          </label>
        )}
        {(uploaded || product.sds) && (
          <div className="work-actions">
            {canExtract && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void extract()}
              >
                {t("sds.extract")}
              </Button>
            )}
            {uploaded && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void attach()}
              >
                {t("sds.attachOnly")}
              </Button>
            )}
          </div>
        )}
        {busy && !uploading && <Loading label={t("sds.processing")} />}
      </CardBody>
      <ConfirmDialog
        open={remove}
        onOpenChange={setRemove}
        title={t("sds.remove")}
        description={t("sds.removeWarning")}
        confirmLabel={t("sds.remove")}
        pending={busy}
        onConfirm={deleteFile}
      />
    </Card>
  );
}

function StatementChanges({
  before,
  after,
  language,
}: {
  before: string;
  after: string;
  language: string;
}) {
  const { t } = useTranslation("work");
  const old = analyzeStatementXmlSegments(before).segments.filter(
    (s) => s.kind === "node" || s.text.trim(),
  );
  const next = analyzeStatementXmlSegments(after).segments.filter(
    (s) => s.kind === "node" || s.text.trim(),
  );
  const oldKeys = old.map((s) => serializeStatementXmlSegments([s]));
  const nextKeys = next.map((s) => serializeStatementXmlSegments([s]));
  return (
    <>
      <p className="work-muted">{t("history.current")}</p>
      {oldKeys.map((text, i) => (
        <div
          key={i}
          className={!nextKeys.includes(text) ? "diff-before" : undefined}
        >
          {!nextKeys.includes(text) && (
            <span className="work-muted">− {t("history.removed")}</span>
          )}
          <StatementRead text={text} language={language} />
        </div>
      ))}
      <p className="work-muted">{t("history.selected")}</p>
      {nextKeys.map((text, i) => (
        <div
          key={i}
          className={!oldKeys.includes(text) ? "diff-after" : undefined}
        >
          {!oldKeys.includes(text) && (
            <span className="work-muted">+ {t("history.added")}</span>
          )}
          <StatementRead text={text} language={language} />
        </div>
      ))}
    </>
  );
}
